// /thanks — Stripe Checkout success landing. Polls /api/purchase-status until
// the webhook has minted the Keygen license, then confirms where the key went.
// Same radar motif as /joined; renders a sane generic state with no session id.
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CRWIcon } from '../components/icons';
import { SiteNav } from '../sections/hero';
import { BigFooter } from '../sections/footer';
import { phCapture, phIdentify } from '../posthog';

type PurchaseState =
  | { status: 'processing' }
  | { status: 'done'; email: string; plan: string }
  | { status: 'slow' };

const PLAN_LABEL: Record<string, string> = {
  monthly: 'Monthly — $29/mo',
  annual: 'Annual — $249/yr',
  founding: 'Founding Edition — yours forever',
};

export function ThanksPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState<PurchaseState>({ status: 'processing' });

  useEffect(() => { document.title = 'Payment received — ClipRadar'; }, []);

  // poll for up to ~30s (webhook fulfilment is usually a second or two)
  useEffect(() => {
    if (!sessionId) { setState({ status: 'slow' }); return; }
    let alive = true;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await fetch(`/api/purchase-status?session_id=${encodeURIComponent(sessionId)}`);
        if (r.ok) {
          const d = await r.json();
          if (!alive) return;
          if (d.status === 'done') {
            setState({ status: 'done', email: d.email, plan: d.plan });
            // The funnel's end: webhook confirmed the Keygen license was minted.
            // Fires once — the poll stops on done. A buyer is a named person
            // (consent rework 2026-08-02) with their plan on the profile.
            phIdentify(d.email, { plan: d.plan });
            phCapture('purchase_completed', { plan: d.plan });
            return;
          }
        }
      } catch { /* keep polling */ }
      if (!alive) return;
      if (tries >= 12) { setState({ status: 'slow' }); return; }
      window.setTimeout(tick, 2500);
    };
    void tick();
    return () => { alive = false; };
  }, [sessionId]);

  const done = state.status === 'done';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SiteNav routed />
      <main style={{ flex: 1, display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden', padding: '140px 24px 80px' }}>
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'radial-gradient(ellipse at center, black, transparent 70%)', WebkitMaskImage: 'radial-gradient(ellipse at center, black, transparent 70%)', pointerEvents: 'none' }}></div>

        <div className="crw-page-in" style={{ position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, maxWidth: 480 }}>
          <div aria-hidden="true" style={{ position: 'relative', width: 132, height: 132, marginBottom: 4, display: 'grid', placeItems: 'center' }}>
            {[1, 0.72, 0.44].map((s, i) => (
              <span key={i} style={{ position: 'absolute', inset: `${(1 - s) * 50}%`, borderRadius: '50%', border: '1px solid rgba(74,227,255,0.18)' }}></span>
            ))}
            <span className="crw-sweep" style={{ position: 'absolute', inset: 0, opacity: 0.55 }}></span>
            {done ? (
              <>
                {/* the radar found it — the check pops in, two echo rings answer */}
                <span className="crw-found-ring"></span>
                <span className="crw-found-ring" style={{ animationDelay: '220ms' }}></span>
                <span className="crw-found" style={{ position: 'relative', display: 'grid', placeItems: 'center', width: 60, height: 60, borderRadius: '50%', color: 'var(--signal-ink)', background: 'radial-gradient(circle at 35% 30%, var(--signal-300), var(--signal-500))', boxShadow: 'var(--glow-signal-strong)' }}>
                  <CRWIcon d="check" size={28} sw={2.6} />
                </span>
              </>
            ) : (
              <span className="crw-pulse" style={{ position: 'relative', width: 14, height: 14, borderRadius: '50%', background: 'var(--signal-400)', boxShadow: '0 0 18px var(--signal-glow)' }}></span>
            )}
          </div>

          <span className="cr-label" style={{ color: 'var(--signal-400)' }}>Payment received</span>
          {/* keyed so the waiting→done copy swap crossfades instead of cutting */}
          <h1 key={done ? 'done' : 'wait'} className="crw-h-grad crw-fade" style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 4.8vw, 50px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)', lineHeight: 1.08, textWrap: 'balance' }}>
            {done ? <>Your key is <span className="crw-shimmer">on its way</span>.</> : <>Minting your <span className="crw-shimmer">license</span>…</>}
          </h1>

          {state.status === 'done' ? (
            <div key="done" className="crw-fade" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <p style={{ margin: 0, maxWidth: 420, fontSize: 15, lineHeight: 1.7, color: 'var(--fg-2)', textWrap: 'pretty' }}>
                Your license key has been emailed to <strong style={{ color: 'var(--fg-1)' }}>{state.email}</strong>. Grab the app, paste the key, and start searching.
              </p>
              <p style={{ margin: '2px 0 0', fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.04em', color: 'var(--signal-300)' }}>{PLAN_LABEL[state.plan] ?? state.plan}</p>
            </div>
          ) : state.status === 'processing' ? (
            <p style={{ margin: 0, maxWidth: 420, fontSize: 15, lineHeight: 1.7, color: 'var(--fg-2)', textWrap: 'pretty' }}>
              Thanks for buying ClipRadar. We’re generating your license key — it lands in your inbox in under a minute.
            </p>
          ) : (
            <p key="slow" className="crw-fade" style={{ margin: 0, maxWidth: 420, fontSize: 15, lineHeight: 1.7, color: 'var(--fg-2)', textWrap: 'pretty' }}>
              Thanks for buying ClipRadar. Your license key is being generated and will land in your inbox shortly — if it hasn’t arrived within the hour, check spam or just reply to your receipt email and we’ll sort it.
            </p>
          )}

          <div style={{ display: 'flex', gap: 18, marginTop: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/download" className="crw-textlink" style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--signal-400)', textDecoration: 'none' }}>
              Download the app →
            </Link>
            <Link to="/account" className="crw-textlink" style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg-2)', textDecoration: 'none' }}>
              Manage your license
            </Link>
            <Link to="/" className="crw-textlink" style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg-3)', textDecoration: 'none' }}>
              Back to homepage
            </Link>
          </div>
        </div>
      </main>
      <BigFooter />
    </div>
  );
}
